/* React base class */
import { Component } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* Icon component */
import Icon from "./Icon"

/* Certificate API: create a certificate and open payment */
import { create } from "../api/Certificate"

/* Translation */
import { translate } from "../translates"

/* Certificate gradient themes */
const THEMES = {
    uni: { name: translate("Универсальный"), gradient: "linear-gradient(135deg, #0a84ff, #6f5bff)" },
    her: { name: translate("Для неё"), gradient: "linear-gradient(135deg, #ff5fa2, #ff9a6b)" },
    him: { name: translate("Для него"), gradient: "linear-gradient(135deg, #0f1729, #3b4a63)" },
    spa: { name: translate("SPA & уход"), gradient: "linear-gradient(135deg, #18a999, #5fd0c0)" },
    sunset: { name: translate("Закат"), gradient: "linear-gradient(135deg, #ff6a00, #ee0979)" },
    night: { name: translate("Ночь"), gradient: "linear-gradient(135deg, #360033, #7b2ff7)" }
}

/* Preset amounts and stepper labels */
const PRESETS = [5000, 10000, 15000, 25000, 50000]

const STEP_LABELS = ["Номинал", "Получатель", "Оплата"]

/* Component CertificateModal */
class CertificateModal extends Component {

    constructor(props) {
        super(props)

        /* State */
        this.state = {
            step: 1,
            amount: 10000,
            custom: "10000",
            theme: "uni",
            recipient: "",
            phone: "+7",
            sender: (props.user && props.user.name) || "",
            message: "",
            delivery: "whatsapp",
            sendAt: "now",
            pay: "card",
            touched: false,
            submitting: false,
            error: null,
            code: this._randomCode()
        }
    }

    componentDidMount = () => {

        /* Lock scroll and listen for Escape */
        document.addEventListener("keydown", this._onKey)
        document.body.style.overflow = "hidden"
    }

    componentWillUnmount = () => {

        /* Restore scroll and drop the listener */
        document.removeEventListener("keydown", this._onKey)
        document.body.style.overflow = ""
    }

    /* Draw On Key */
    _onKey = (event) => {

        /* Close on Escape */
        if (event.key === "Escape") {
            this.props.onClose()
        }
    }

    /* Draw Random Code */
    _randomCode() {

        /* Preview code shown until the real one is issued */
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
        const part = (length) => Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join("")
        return `BKF-${part(4)}-${part(4)}`
    }

    /* Draw Money */
    _money = (number) => `${Number(number || 0).toLocaleString("ru-RU")} ₸`

    /* Draw Value */
    _value = () => {

        /* Fields */
        const { amount, custom } = this.state

        /* Custom amount capped at 100 000 ₸ */
        if (custom) {
            return Math.min(100000, Math.max(0, parseInt(custom.replace(/\D/g, ""), 10) || 0))
        }
        return amount
    }

    /* Draw On Amount */
    _onAmount = (event) => {

        /* Keep digits only, capped at 100 000 */
        const digits = event.target.value.replace(/\D/g, "")
        const capped = Math.min(100000, parseInt(digits, 10) || 0)
        this.setState({ custom: capped ? String(capped) : "" })
    }

    /* Draw Format Phone */
    _formatPhone = (raw) => {

        /* Progressive mask without trailing separators, so backspace always deletes */
        let digits = raw.replace(/\D/g, "")
        if (digits.startsWith("8")) {
            digits = "7" + digits.slice(1)
        }
        if (digits.startsWith("7")) {
            digits = digits.slice(1)
        }
        digits = digits.slice(0, 10)
        let result = "+7"
        if (digits.length > 0) {
            result += " (" + digits.slice(0, 3)
        }
        if (digits.length > 3) {
            result += ") " + digits.slice(3, 6)
        }
        if (digits.length > 6) {
            result += "-" + digits.slice(6, 8)
        }
        if (digits.length > 8) {
            result += "-" + digits.slice(8, 10)
        }
        return result
    }

    /* Draw On Phone Change */
    _onPhoneChange = (event) => {

        /* Reformat on every keystroke */
        this.setState({ phone: this._formatPhone(event.target.value) })
    }

    /* Draw To Step 2 */
    _toStep2 = () => {

        /* Require the minimum nominal */
        if (this._value() < 1000) {
            return
        }
        this.setState({ step: 2 })
    }

    /* Draw To Step 3 */
    _toStep3 = () => {

        /* Fields */
        const { recipient, phone } = this.state

        /* Validate recipient and phone */
        const phoneDigits = phone.replace(/\D/g, "")
        if (!recipient.trim() || phoneDigits.length < 11) {
            this.setState({ touched: true })
            return
        }
        this.setState({ step: 3, touched: false })
    }

    /* Draw To Step 4 */
    _toStep4 = () => {

        /* Fields */
        const { recipient, phone, sender, message, theme, delivery, sendAt, submitting } = this.state

        /* Guard against double submit */
        if (submitting) {
            return
        }
        this.setState({ submitting: true, error: null })

        /* Build the request body */
        const body = {
            amount: this._value(),
            theme,
            recipient: recipient.trim(),
            phone,
            sender: sender.trim() || null,
            message: message.trim() || null,
            delivery,
            sendAt
        }

        /* Create the certificate and go to the payment gateway */
        create(this.props.token, body).then((response) => {
            if (response && response.status === 201 && response.data && response.data.payUrl) {
                /* Certificate is activated after successful payment */
                window.location.href = response.data.payUrl
            }
            else {
                const error = response && response.data && response.data.error ? response.data.error.message : translate("Не удалось оформить сертификат. Попробуйте позже.")
                this.setState({ submitting: false, error })
            }
        }).catch(() => {
            this.setState({ submitting: false, error: translate("Не удалось оформить сертификат. Попробуйте позже.") })
        })
    }

    /* Draw Copy Code */
    _copyCode = () => {

        /* Copy the code through a throwaway textarea */
        const textarea = document.createElement("textarea")
        textarea.value = this.state.code
        textarea.style.position = "fixed"
        textarea.style.opacity = "0"
        document.body.appendChild(textarea)
        textarea.select()
        try {
            document.execCommand("copy")
        }
        catch (error) {
            /* silent */
        }
        document.body.removeChild(textarea)
    }

    /* Draw Certificate */
    _certificate = () => {

        /* Fields */
        const { theme, recipient, step, code } = this.state
        const themeData = THEMES[theme]

        return (
            <div className="cert" style={{ background: themeData.gradient }}>
                <div className="cert__shine" />
                <div className="cert__top">
                    <span className="cert__brand">bookify</span>
                    <span className="cert__chip">{translate("Подарочный сертификат")}</span>
                </div>
                <div className="cert__amount">{this._money(this._value())}</div>
                <div className="cert__foot">
                    <div>
                        <div className="cert__label">{translate("Кому")}</div>
                        <div className="cert__val">{recipient || translate("Имя получателя")}</div>
                    </div>
                    <div>
                        <div className="cert__label">{translate("Код")}</div>
                        <div className="cert__val">{step === 4 ? code : "BKF · ••••"}</div>
                    </div>
                </div>
            </div>
        )
    }

    /* Draw Step 1 */
    _step1 = () => {

        /* Fields */
        const { custom } = this.state
        const value = this._value()

        return (
            <div className="cert-step">
                <h4 className="cert-h">{translate("Выберите номинал")}</h4>
                <div className="cert-amounts">
                    {PRESETS.map((preset) => (
                        <button
                            key={`${preset}`}
                            className={`cert-amount${value === preset ? " is-active" : ""}`}
                            onClick={() => this.setState({ amount: preset, custom: String(preset) })}
                        >
                            {this._money(preset)}
                        </button>
                    ))}
                </div>
                <div className="field certform__gap">
                    <label className="field__label">{translate("Своя сумма (до 100 000 ₸)")}</label>
                    <input
                        className="input"
                        inputMode="numeric"
                        placeholder={translate("Например, 30 000")}
                        value={custom ? Number(custom.replace(/\D/g, "")).toLocaleString("ru-RU") : ""}
                        onChange={this._onAmount}
                    />
                </div>
                <button className="btn btn--block btn--lg certform__continue" disabled={value < 1000} onClick={this._toStep2}>
                    {translate("Продолжить")} · {this._money(value)}
                </button>
                {value < 1000 && <p className="cert-hint">{translate("Минимальный номинал — 1 000 ₸")}</p>}
            </div>
        )
    }

    /* Draw Step 2 */
    _step2 = () => {

        /* Fields */
        const { recipient, phone, sender, message, touched } = this.state
        const phoneDigits = phone.replace(/\D/g, "")
        const recipientError = touched && !recipient.trim()
        const phoneError = touched && phoneDigits.length < 11
        const suggestions = ["С Днём рождения! 🎁", "С праздником, ты заслуживаешь лучшего ✨", "Просто порадовать 💛"]

        return (
            <div className="cert-step">
                <h4 className="cert-h">{translate("Кому дарим")}</h4>
                <div className="field">
                    <label className="field__label">{translate("Имя получателя")}</label>
                    <input
                        className={`input${recipientError ? " input--error" : ""}`}
                        placeholder={translate("Например, Айгерим")}
                        value={recipient}
                        onChange={(event) => this.setState({ recipient: event.target.value })}
                    />
                    {recipientError && <div className="field__err">{translate("Укажите имя получателя")}</div>}
                </div>
                <div className="field">
                    <label className="field__label">{translate("Телефон получателя")}</label>
                    <input
                        className={`input${phoneError ? " input--error" : ""}`}
                        inputMode="tel"
                        placeholder="+7 (___) ___-__-__"
                        value={phone}
                        onChange={this._onPhoneChange}
                    />
                    {phoneError && <div className="field__err">{translate("Введите корректный номер")}</div>}
                </div>
                <div className="field">
                    <label className="field__label">{translate("От кого")}</label>
                    <input
                        className="input"
                        placeholder={translate("Ваше имя")}
                        value={sender}
                        onChange={(event) => this.setState({ sender: event.target.value })}
                    />
                </div>
                <div className="field">
                    <label className="field__label">{translate("Поздравление")}</label>
                    <textarea
                        className="input certform__textarea"
                        placeholder={translate("С праздником! Дарю тебе заботу о себе ✨")}
                        value={message}
                        onChange={(event) => this.setState({ message: event.target.value })}
                    />
                    <div className="cert-suggest">
                        {suggestions.map((suggestion) => (
                            <button key={suggestion} onClick={() => this.setState({ message: suggestion })}>
                                {translate(suggestion)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="cert-actions">
                    <button className="btn btn--ghost" onClick={() => this.setState({ step: 1, touched: false })}>{translate("Назад")}</button>
                    <button className="btn btn--lg certform__grow" onClick={this._toStep3}>{translate("Продолжить")}</button>
                </div>
            </div>
        )
    }

    /* Draw Step 3 */
    _step3 = () => {

        /* Fields */
        const { submitting, error } = this.state
        const value = this._value()

        return (
            <div className="cert-step">
                <h4 className="cert-h">{translate("Оплата")}</h4>
                <div className="cert-summary">
                    <div><span>{translate("Номинал")}</span><b>{this._money(value)}</b></div>
                    <div><span>{translate("Комиссия")}</span><b className="certform__free">0 ₸</b></div>
                    <div className="cert-summary__total"><span>{translate("Итого")}</span><b>{this._money(value)}</b></div>
                </div>
                {error && (
                    <div className="field__err certform__error">{error}</div>
                )}
                <div className="cert-actions">
                    <button className="btn btn--ghost" disabled={submitting} onClick={() => this.setState({ step: 2 })}>{translate("Назад")}</button>
                    <button className="btn btn--lg certform__grow" disabled={submitting} onClick={this._toStep4}>
                        {submitting ? translate("Оформляем...") : `${translate("Оплатить")} ${this._money(value)}`}
                    </button>
                </div>
            </div>
        )
    }

    /* Draw Step 4 */
    _step4 = () => {

        /* Fields */
        const { recipient, phone, delivery, sendAt, code } = this.state

        /* Build the confirmation text */
        const deliveryLabels = { whatsapp: "WhatsApp", sms: "SMS", email: "Email" }
        const deliveryLabel = deliveryLabels[delivery]
        let sentText = ""
        if (sendAt === "now") {
            sentText = `${translate("Отправили в")} ${deliveryLabel}${recipient ? ` ${translate("для")} ${recipient}` : ""} ${translate("на номер")} ${phone}.`
        }
        else {
            sentText = `${translate("Доставим в")} ${deliveryLabel}${recipient ? ` ${translate("для")} ${recipient}` : ""} ${translate("в день праздника.")}`
        }

        return (
            <div className="cert-done">
                <div className="cert-done__check"><Icon name="check" size={32} /></div>
                <h3>{translate("Сертификат оформлен!")}</h3>
                <p>{sentText}</p>
                <div className="cert-code">
                    <div>
                        <div className="cert__label certform__code-label">{translate("Код сертификата")}</div>
                        <div className="cert-code__val">{code}</div>
                    </div>
                    <button className="btn btn--ghost certform__copy" onClick={this._copyCode}>
                        <Icon name="share" size={14} /> {translate("Копировать")}
                    </button>
                </div>
                <button className="btn btn--block btn--lg" onClick={this.props.onClose}>{translate("Готово")}</button>
            </div>
        )
    }

    render = () => {

        /* Fields */
        const { onClose } = this.props
        const { step, theme } = this.state

        return (
            <div className="modal-backdrop" onClick={onClose}>
                <div className="modal modal--lg" onClick={(event) => event.stopPropagation()}>
                    <div className="modal__header">
                        <h3 className="modal__title">{translate("Подарочный сертификат")}</h3>
                        <button className="modal__close" onClick={onClose} aria-label={translate("Закрыть")}>
                            <Icon name="close" size={20} />
                        </button>
                    </div>
                    <div className="modal__body cert-modal">
                        <div className="cert-modal__preview">
                            {this._certificate()}
                            {step < 4 && (
                                <div className="cert-themes">
                                    {Object.entries(THEMES).map(([key, themeData]) => (
                                        <button
                                            key={key}
                                            className={`cert-theme${theme === key ? " is-active" : ""}`}
                                            style={{ background: themeData.gradient }}
                                            onClick={() => this.setState({ theme: key })}
                                            title={themeData.name}
                                            aria-label={themeData.name}
                                        >
                                            {theme === key && <Icon name="check" size={14} />}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <p className="cert-note">
                                {translate("Действует 12 месяцев · принимается во всех салонах Bookify")}
                            </p>
                        </div>
                        <div className="cert-modal__form">
                            {step < 4 && (
                                <div className="stepper">
                                    {STEP_LABELS.map((label, index) => (
                                        <div
                                            key={label}
                                            className={`stepper__item${step === index + 1 ? " is-active" : ""}${step > index + 1 ? " is-done" : ""}`}
                                        >
                                            <span className="stepper__dot">
                                                {step > index + 1 ? <Icon name="check" size={13} /> : index + 1}
                                            </span>
                                            <span className="stepper__label">{translate(label)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {step === 1 && this._step1()}
                            {step === 2 && this._step2()}
                            {step === 3 && this._step3()}
                            {step === 4 && this._step4()}
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        user: state.user,
        lang: state.lang
    }
}

/* Export */
export default connect(mapStateToProps)(CertificateModal)
